// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as Rematrix from 'rematrix';
import memoizeOne from 'memoize-one';
import { createStructuredSelector } from 'reselect';

// Utils
import { getViewportOffset } from '../../utils/domUtil';
import rafThrottle from '../../../common/utils/lib/rafThrottle';
import { getScrollAsPoint } from '../../utils/dom/scrollableParentUtils';
import * as pointLib from '../../../common/maths/geometry/point';
import * as rematrixLib from '../../../common/maths/matrix/matrixUtils';
import { convertMatrixToCssTransform } from '../../utils/cssUtil';

// Measurements
import measurementsRegistry from '../../components/measurementsStore/measurementsRegistry';

// Selectors
import { getCanvasSizePojo } from '../store/canvasSizeSelector';
import { canvasZoomScaleSelector, canvasZoomTranslationPxPojoSelector } from '../store/canvasZoomSelector';

// Styles
import './CanvasViewportReplica.scss';

const getScrollAreaStyle = memoizeOne((canvasSize, zoomScale, zoomTranslationPx) => {
    let transformationMatrix = Rematrix.scale(zoomScale);
    transformationMatrix = rematrixLib.setTranslation(transformationMatrix, zoomTranslationPx);

    return {
        ...canvasSize,
        ...convertMatrixToCssTransform(transformationMatrix),
    };
});

const mapStateToProps = createStructuredSelector({
    canvasSize: getCanvasSizePojo,
    zoomScale: canvasZoomScaleSelector,
    zoomTranslationPx: canvasZoomTranslationPxPojoSelector,
});

/**
 * The ConnectedElementDragLayer positions elements based on their x,y locations in the store,
 * using the ElementPositioner.
 * Now the ConnectedElementDragLayer does not sit within the Canvas DOM element, we need to translate
 * the layer contents to the top left position of the Canvas, otherwise the toolbar and header would
 * mean the connected elements don't sit in their correct locations.
 */
const CanvasViewportReplica = (props) => {
    const { children, currentBoardId, canvasSize, zoomScale, zoomTranslationPx } = props;

    const [translationStyles, setTranslationStyles] = React.useState({ top: 0, left: 0 });
    const [scrollDiff, setScrollDiff] = React.useState({ x: 0, y: 0 });

    const initialScrollPoint = React.useRef();
    const domRef = React.useRef();

    const onScroll = (event) => {
        if (!domRef.current) return;

        domRef.current.scrollTop = event.target.scrollTop;
        domRef.current.scrollLeft = event.target.scrollLeft;

        const newScrollPoint = getScrollAsPoint(event.target);

        const newScrollDiff = pointLib.difference(initialScrollPoint.current, newScrollPoint);
        setScrollDiff(newScrollDiff);
    };

    const throttledOnScroll = React.useCallback(rafThrottle(onScroll), []);

    React.useEffect(() => {
        const canvasViewportEl = measurementsRegistry.getCanvasViewportDomElement();

        if (!canvasViewportEl) return;

        const boundingRect = canvasViewportEl.getBoundingClientRect();
        const viewportOffset = getViewportOffset();

        setTranslationStyles({
            left: boundingRect.left + viewportOffset.x,
            top: boundingRect.top + viewportOffset.y,
            width: boundingRect.width,
            height: boundingRect.height,
        });

        initialScrollPoint.current = getScrollAsPoint(canvasViewportEl);

        canvasViewportEl.addEventListener('scroll', throttledOnScroll);

        // Need to wait for the canvas position translator to be sized correctly before scrolling
        // to the correct point
        requestAnimationFrame(() => {
            if (!canvasViewportEl) return;
            if (!domRef.current) return;

            domRef.current.scrollTop = canvasViewportEl.scrollTop;
            domRef.current.scrollLeft = canvasViewportEl.scrollLeft;
        });

        return () => {
            canvasViewportEl.removeEventListener('scroll', throttledOnScroll);
        };
    }, [currentBoardId]);

    return (
        <div ref={domRef} className="CanvasViewportReplica" style={translationStyles} data-ignore-touch-scroll>
            <div
                className="canvas-document-replica"
                style={getScrollAreaStyle(canvasSize, zoomScale, zoomTranslationPx)}
            >
                {React.Children.map(children, (child) => React.cloneElement(child, { scrollDiff }))}
            </div>
        </div>
    );
};

CanvasViewportReplica.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    currentBoardId: PropTypes.string,
    canvasSize: PropTypes.object,
    zoomScale: PropTypes.number,
    zoomTranslationPx: PropTypes.object,
};

export default connect(mapStateToProps)(CanvasViewportReplica);
